import {
  beneficiaries,
  businessPayments,
  fmtEur,
} from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

export function renderSend(ctx: Ctx) {
  const sentRows = businessPayments
    .filter((p) => p.direction === "out" && p.rail !== "internal" && p.rail !== "card")
    .slice(0, 6);

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Business Account<span class="page-eyebrow-rule"></span>Send</span>
        <h1 class="page-title">Send a business payment</h1>
      </div>
      <div class="page-head-meta">
        <span class="page-head-date">Available balance</span>
        <span style="font-size: 0.95rem; font-weight: 700; color: var(--ink-primary); font-variant-numeric: tabular-nums;">€482,015.20</span>
        <span style="font-size: 0.7rem; color: var(--ink-subtle);">Acc. EE38…417 · Default</span>
      </div>
    </section>

    <section class="grid-2-1">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">New payment</div>
            <h2 class="panel-title">Choose rail and recipient</h2>
            <p class="panel-sub">SEPA Instant settles in &lt; 60s in the EU. SWIFT for non-EU rails.</p>
          </div>
          <div class="pill-toggle" id="rail-toggle">
            <button class="is-active" data-rail="sepa">SEPA · Instant</button>
            <button data-rail="swift">SWIFT</button>
            <button data-rail="internal">Internal</button>
          </div>
        </div>

        <div class="panel-body">
          <div class="form-grid" id="send-form">
            <div class="field form-grid-full">
              <label class="field-label" for="s-beneficiary">Beneficiary</label>
              <select class="field-control" id="s-beneficiary">
                <option value="">— New beneficiary —</option>
                ${beneficiaries
                  .map(
                    (b) =>
                      `<option value="${b.id}">${b.name} · ${b.country}</option>`,
                  )
                  .join("")}
              </select>
              <span class="field-help">Choose a saved beneficiary or fill the fields below for a new one.</span>
            </div>

            <div class="field form-grid-full" data-rail-show="sepa">
              <label class="field-label" for="s-iban">IBAN</label>
              <input class="field-control" id="s-iban" placeholder="EE38 7700 4220 0091 0712" value="EE38 7700 4220 0091 0712" />
              <span class="field-help">Format: 2-letter country + 2 check digits + bank code.</span>
            </div>

            <div class="field form-grid-full" data-rail-show="swift" style="display: none;">
              <label class="field-label" for="s-account">Account number</label>
              <input class="field-control" id="s-account" placeholder="UK ✓ · Account number" />
            </div>
            <div class="field" data-rail-show="swift" style="display: none;">
              <label class="field-label" for="s-bic">BIC / SWIFT code</label>
              <input class="field-control" id="s-bic" placeholder="BARCGB22XXX" />
            </div>
            <div class="field" data-rail-show="swift" style="display: none;">
              <label class="field-label" for="s-currency">Currency</label>
              <select class="field-control" id="s-currency">
                <option>GBP · British Pound</option>
                <option>USD · US Dollar</option>
                <option>CHF · Swiss Franc</option>
                <option>JPY · Japanese Yen</option>
              </select>
            </div>

            <div class="field form-grid-full" data-rail-show="internal" style="display: none;">
              <label class="field-label" for="s-internal-target">Internal account</label>
              <select class="field-control" id="s-internal-target">
                <option>Pay-Out balance · top up liquidity</option>
                <option>Crypto wallet · USDC (Solana)</option>
                <option>Reserve account · EE54…388</option>
              </select>
            </div>

            <div class="field form-grid-full">
              <label class="field-label" for="s-amount">Amount</label>
              <input class="field-control is-amount" id="s-amount" value="€ 4,250.00" />
              <span class="field-help" id="s-amount-help">SEPA Instant fee · €0.30. Net to beneficiary · €4,249.70</span>
            </div>

            <div class="field">
              <label class="field-label" for="s-execute">Execute</label>
              <select class="field-control" id="s-execute">
                <option>Immediately</option>
                <option>Next clearing window</option>
                <option>Schedule for…</option>
              </select>
            </div>

            <div class="field">
              <label class="field-label" for="s-charge">Charge bearer</label>
              <select class="field-control" id="s-charge">
                <option>Shared (SHA)</option>
                <option>Sender pays (OUR)</option>
                <option>Beneficiary pays (BEN)</option>
              </select>
            </div>

            <div class="field form-grid-full">
              <label class="field-label" for="s-ref">Reference / message</label>
              <input class="field-control" id="s-ref" placeholder="INV-2026-0481 · Wholesale fabric May" />
              <span class="field-help">Up to 140 characters · shown to the beneficiary's bank.</span>
            </div>
          </div>

          <div class="send-summary" id="send-summary" data-rail="sepa">
            <div class="send-summary-row">
              <span>Rail</span>
              <span><strong>SEPA Instant · EUR</strong></span>
            </div>
            <div class="send-summary-row">
              <span>Estimated arrival</span>
              <span><strong>Under 60 seconds</strong></span>
            </div>
            <div class="send-summary-row">
              <span>Fee</span>
              <span><strong>€0.30</strong></span>
            </div>
            <div class="send-summary-row is-total">
              <span>Net to beneficiary</span>
              <span><strong style="color: var(--accent);">€4,249.70</strong></span>
            </div>
          </div>

          <div style="display: flex; gap: 0.6rem; margin-top: 1.5rem; justify-content: flex-end;">
            <button class="btn btn-ghost btn-sm">Save as draft</button>
            <button class="btn btn-primary">Sign &amp; send ${icon("arrowRight", 11)}</button>
          </div>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Recently sent</div>
            <h2 class="panel-title">Last business payments</h2>
          </div>
        </div>
        <div class="ledger-table">
          ${sentRows
            .map(
              (p) => `<div class="ledger-table-row" style="grid-template-columns: 70px minmax(0, 1fr) auto;">
            <div class="col-date" style="font-size: 0.66rem;">
              <strong>${p.date}</strong>
              ${p.time}
            </div>
            <div class="col-name" style="min-width: 0;">
              <strong>${p.beneficiary}</strong>
              <span>${p.railLabel} · ${p.reference}</span>
            </div>
            <div class="col-amount is-out" style="font-size: 0.85rem;">
              −${fmtEur(p.amountEur, { compact: p.amountEur > 9999 })}
              ${p.amountText ? `<span class="col-amount-sub">${p.amountText}</span>` : ""}
            </div>
          </div>`,
            )
            .join("")}
        </div>
        <div class="ledger-table-foot">
          <a class="link-arrow" data-route="business-statements">Open statements ${icon("arrowRight", 13)}</a>
        </div>
      </article>
    </section>
  `;

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });

  // Rail toggle — show/hide rail-specific fields and update summary
  const summary = ctx.canvas.querySelector<HTMLElement>("#send-summary");
  const help = ctx.canvas.querySelector<HTMLElement>("#s-amount-help");

  ctx.canvas
    .querySelectorAll<HTMLButtonElement>("#rail-toggle button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        ctx.canvas
          .querySelectorAll("#rail-toggle .is-active")
          .forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
        const rail = btn.dataset.rail!;
        ctx.canvas
          .querySelectorAll<HTMLElement>("[data-rail-show]")
          .forEach((el) => {
            el.style.display =
              el.dataset.railShow === rail ? "" : "none";
          });

        // Update summary block + help text per rail
        if (summary) {
          summary.dataset.rail = rail;
          if (rail === "sepa") {
            summary.innerHTML = `
              <div class="send-summary-row"><span>Rail</span><span><strong>SEPA Instant · EUR</strong></span></div>
              <div class="send-summary-row"><span>Estimated arrival</span><span><strong>Under 60 seconds</strong></span></div>
              <div class="send-summary-row"><span>Fee</span><span><strong>€0.30</strong></span></div>
              <div class="send-summary-row is-total"><span>Net to beneficiary</span><span><strong style="color: var(--accent);">€4,249.70</strong></span></div>`;
            if (help)
              help.textContent =
                "SEPA Instant fee · €0.30. Net to beneficiary · €4,249.70";
          } else if (rail === "swift") {
            summary.innerHTML = `
              <div class="send-summary-row"><span>Rail</span><span><strong>SWIFT · GBP</strong></span></div>
              <div class="send-summary-row"><span>Estimated arrival</span><span><strong>1 – 3 business days</strong></span></div>
              <div class="send-summary-row"><span>FX rate · EUR/GBP</span><span><strong>0.8442</strong></span></div>
              <div class="send-summary-row"><span>Fee · OUR</span><span><strong>€18.00</strong></span></div>
              <div class="send-summary-row is-total"><span>Net to beneficiary</span><span><strong style="color: var(--accent);">GBP 3,587.85</strong></span></div>`;
            if (help)
              help.textContent =
                "SWIFT fee · €18.00 · subject to correspondent-bank deductions.";
          } else {
            summary.innerHTML = `
              <div class="send-summary-row"><span>Rail</span><span><strong>Internal · same merchant</strong></span></div>
              <div class="send-summary-row"><span>Estimated arrival</span><span><strong>Instant</strong></span></div>
              <div class="send-summary-row"><span>Fee</span><span><strong>€0.00</strong></span></div>
              <div class="send-summary-row is-total"><span>Net</span><span><strong style="color: var(--accent);">€4,250.00</strong></span></div>`;
            if (help)
              help.textContent =
                "Internal transfers between your accounts settle instantly with no fee.";
          }
        }
      });
    });
}
